import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useSession } from "@/auth/session";
import {
  createContract,
  createCustomer,
  createMeter,
  listContracts,
  listCustomers,
  listSectors,
  listTariffs,
  type CustomerFormPayload,
} from "@/lib/api";

type Props = {
  onClose: () => void;
  onImported: () => void;
};

type ImportRow = {
  tipo: string;
  rut: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  direccion: string;
  telefono: string;
  email: string;
  contrato: string;
  direccionContrato: string;
  sector: string;
  tarifa: string;
  medidor: string;
  sello: string;
  fechaIngreso: string;
};

const CSV_HEADERS = [
  "tipo (0,1,2)",
  "rut",
  "nombre",
  "apellido paterno",
  "apellido materno",
  "direccion",
  "telefono",
  "email",
  "contrato",
  "direccion_contrato",
  "sector",
  "tarifa",
  "medidor",
  "sello",
  "fecha_ingreso",
];

export default function ImportClientesModal({ onClose, onImported }: Props) {
  const { session } = useSession();
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [report, setReport] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const canProcess = useMemo(() => !!session && !!file && !isProcessing, [file, isProcessing, session]);

  function downloadTemplate(): void {
    const csv = `${CSV_HEADERS.join(";")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "formato_importacion_clientes_contratos.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  async function readRows(selected: File): Promise<ImportRow[]> {
    const isXlsx = /\.(xlsx|xls)$/i.test(selected.name);
    if (isXlsx) return readRowsFromXlsx(selected);
    return readRowsFromCsv(selected);
  }

  async function readRowsFromXlsx(selected: File): Promise<ImportRow[]> {
    const buffer = await selected.arrayBuffer();
    const wb = XLSX.read(new Uint8Array(buffer), { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: "" });
    return raw.map((row) => ({
      tipo: String(row["tipo"] ?? "0"),
      rut: String(row["rut"] ?? ""),
      nombre: String(row["razon"] ?? row["nombre"] ?? ""),
      apellidoPaterno: String(row["paterno"] ?? row["apellido paterno"] ?? ""),
      apellidoMaterno: String(row["materno"] ?? row["apellido materno"] ?? ""),
      direccion: String(row["direccion"] ?? ""),
      telefono: String(row["telefono"] ?? ""),
      email: String(row["email"] ?? ""),
      contrato: String(row["contrato"] ?? "").replace(/\.0+$/, ""),
      direccionContrato: String(row["direccion_contrato"] ?? row["direccion"] ?? ""),
      sector: String(row["sector"] ?? ""),
      tarifa: String(row["tarifa"] ?? ""),
      medidor: String(row["medidor"] ?? ""),
      sello: String(row["sello"] ?? ""),
      fechaIngreso: parseExcelDate(row["fecha_ingreso"]),
    }));
  }

  async function readRowsFromCsv(selected: File): Promise<ImportRow[]> {
    const content = await selected.text();
    const lines = content.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length <= 1) return [];
    const rows = lines.slice(1).map((line) => parseCsvLine(line, ";"));
    return rows.map((cols) => ({
      tipo: asString(cols[0]),
      rut: asString(cols[1]),
      nombre: asString(cols[2]),
      apellidoPaterno: asString(cols[3]),
      apellidoMaterno: asString(cols[4]),
      direccion: asString(cols[5]),
      telefono: asString(cols[6]),
      email: asString(cols[7]),
      contrato: asString(cols[8]),
      direccionContrato: asString(cols[9]),
      sector: asString(cols[10]),
      tarifa: asString(cols[11]),
      medidor: asString(cols[12]),
      sello: asString(cols[13]),
      fechaIngreso: asString(cols[14]),
    }));
  }

  async function processImport(): Promise<void> {
    if (!session || !file) return;

    setIsProcessing(true);
    setError(null);
    setReport([]);

    try {
      const rows = await readRows(file);
      const [customers, contracts, tariffs, sectors] = await Promise.all([
        listCustomers(session),
        listContracts(session),
        listTariffs(session),
        listSectors(session),
      ]);

      const customerMap = new Map(customers.map((c) => [normalizeRut(c.rut), c]));
      const contractNumbers = new Set(contracts.map((c) => normalizeContractNumber(c.contractNumber).toUpperCase()));

      const tariffByKey = new Map<string, string>();
      tariffs.forEach((t) => {
        tariffByKey.set(t.id, t.id);
        tariffByKey.set(t.code.trim().toUpperCase(), t.id);
        tariffByKey.set(t.name.trim().toUpperCase(), t.id);
      });

      const sectorByKey = new Map<string, string>();
      sectors.forEach((s) => {
        sectorByKey.set(s.name.trim().toUpperCase(), s.name);
        sectorByKey.set(s.code.trim().toUpperCase(), s.name);
      });

      let createdCustomers = 0;
      let createdContracts = 0;
      let skipped = 0;

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const line = index + 2;
        try {
          if (!row.rut || !row.contrato || !row.tarifa) {
            skipped += 1;
            setReport((prev) => [...prev, `Fila ${line}: omitida (faltan rut/contrato/tarifa)`]);
            continue;
          }

          const rutParsed = splitRut(row.rut);
          const rutKey = `${rutParsed.rut}-${rutParsed.dv}`;
          let customerId = customerMap.get(rutKey)?.id;

          if (!customerId) {
            const customerType = mapTipo(row.tipo);
            const payload: CustomerFormPayload = {
              customerType,
              rut: rutParsed.rut,
              dv: rutParsed.dv,
              firstName: customerType === "domiciliario" ? row.nombre : undefined,
              lastName: customerType === "domiciliario" ? row.apellidoPaterno : undefined,
              middleName: customerType === "domiciliario" ? row.apellidoMaterno : undefined,
              businessName: customerType !== "domiciliario" ? row.nombre : undefined,
              address: row.direccion || row.direccionContrato,
              phone: row.telefono || undefined,
              email: row.email || undefined,
              joinDate: normalizeDate(row.fechaIngreso),
            };
            const createdCustomer = await createCustomer(session, payload);
            customerId = createdCustomer.id;
            customerMap.set(rutKey, createdCustomer);
            createdCustomers += 1;
          }

          const contractNumber = normalizeContractNumber(row.contrato).toUpperCase();
          if (contractNumbers.has(contractNumber)) {
            skipped += 1;
            setReport((prev) => [...prev, `Fila ${line}: contrato ${contractNumber} ya existe`]);
            continue;
          }

          const tariffId = tariffByKey.get(row.tarifa.trim().toUpperCase()) ?? tariffByKey.get(row.tarifa.trim());
          if (!tariffId) {
            skipped += 1;
            setReport((prev) => [...prev, `Fila ${line}: tarifa no encontrada (${row.tarifa})`]);
            continue;
          }

          const meterSerial = row.medidor.trim() || buildProvisionalMeterSerial(contractNumber, rutKey);
          const meter = await createMeter(session, {
            meterType: "mecanico",
            serialNumber: meterSerial,
            sealNumber: row.sello.trim() || undefined,
            status: "active",
          });

          const sectorName = sectorByKey.get(row.sector.trim().toUpperCase()) ?? row.sector.trim();

          await createContract(session, {
            customerId,
            meterId: meter.id,
            tariffId,
            contractNumber,
            contractDate: normalizeDate(row.fechaIngreso),
            startAddress: row.direccionContrato || row.direccion,
            sector: sectorName || undefined,
            status: "active",
            billingDocumentType: "39",
          });

          contractNumbers.add(contractNumber);
          createdContracts += 1;
          if (!row.medidor.trim()) {
            setReport((prev) => [...prev, `Fila ${line}: contrato ${contractNumber} importado con medidor provisional ${meterSerial}`]);
          }
        } catch (rowError) {
          skipped += 1;
          setReport((prev) => [
            ...prev,
            `Fila ${line}: error — ${rowError instanceof Error ? rowError.message : "desconocido"}`,
          ]);
        }
      }

      setReport((prev) => [
        ...prev,
        `─── Resumen: ${createdCustomers} clientes creados, ${createdContracts} contratos creados, ${skipped} omitidos`,
      ]);
      onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo importar archivo");
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Importar clientes y contratos</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Acepta Excel (.xlsx) o CSV (;). Formatos soportados: columnas estándar o formato <code className="bg-gray-100 px-1 rounded">rut / razon / paterno / materno / contrato / sector / tarifa</code>.
          </p>

          <div className="flex items-center gap-3">
            <button onClick={downloadTemplate} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
              Descargar formato CSV
            </button>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-sm"
            />
          </div>

          {file && <div className="text-xs text-gray-500">Archivo: {file.name}</div>}
          {error && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

          <div className="max-h-56 overflow-y-auto bg-gray-50 rounded-lg p-3 text-xs text-gray-700 space-y-1">
            {report.length === 0 ? (
              <div className="text-gray-400">Sin ejecución aún.</div>
            ) : (
              report.map((line, idx) => (
                <div key={idx} className={line.startsWith("───") ? "font-semibold text-gray-800 pt-1" : line.includes("error") ? "text-red-600" : ""}>
                  {line}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg">Cerrar</button>
          <button
            onClick={processImport}
            disabled={!canProcess}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-60"
          >
            {isProcessing ? "Procesando..." : "Importar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function parseExcelDate(raw: unknown): string {
  if (typeof raw === "number") {
    const d = XLSX.SSF.parse_date_code(raw);
    return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  return String(raw ?? "");
}

function parseCsvLine(line: string, sep: string): string[] {
  const out: string[] = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') { current += '"'; i += 1; }
      else quoted = !quoted;
    } else if (ch === sep && !quoted) {
      out.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  out.push(current.trim());
  return out;
}

function mapTipo(value: string): "domiciliario" | "empresa" | "institucion" {
  const v = value.trim();
  if (v === "1") return "empresa";
  if (v === "2") return "institucion";
  return "domiciliario";
}

function normalizeRut(value: string): string {
  const parsed = splitRut(value);
  return `${parsed.rut}-${parsed.dv}`;
}

function normalizeContractNumber(value: string): string {
  return value.trim().replace(/\.0+$/u, "");
}

function buildProvisionalMeterSerial(contractNumber: string, rutKey: string): string {
  return `AUTO-${normalizeContractNumber(contractNumber)}-${rutKey.replace(/[^0-9A-Za-z]/g, "")}`;
}

function splitRut(input: string): { rut: string; dv: string } {
  const clean = input.replace(/\./g, "").trim().toUpperCase();
  if (clean.includes("-")) {
    const [rut, dv] = clean.split("-");
    return { rut: onlyDigits(rut), dv: (dv || "").toUpperCase() || "0" };
  }
  const rut = onlyDigits(clean.slice(0, -1));
  const dv = clean.slice(-1).toUpperCase() || "0";
  return { rut, dv };
}

function normalizeDate(value: string): string {
  const raw = value.trim();
  if (!raw) return new Date().toISOString().slice(0, 10);
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const parts = raw.split(/[\/-]/);
  if (parts.length === 3) {
    const [a, b, c] = parts;
    if (a.length === 4) return `${a}-${b.padStart(2, "0")}-${c.padStart(2, "0")}`;
    return `${c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
  }
  return new Date().toISOString().slice(0, 10);
}

function onlyDigits(value: string): string {
  return value.replace(/\D+/g, "");
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}
